import React, { useContext, useEffect, useState } from 'react';
import { bool, func, number, shape } from 'prop-types';
import { useTheme } from '@mui/material/styles';
import * as R from 'ramda';
import { Box, Button, Portal } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { addDays } from 'date-fns';

// Components
import { DrawerLayoutWrapper } from 'common';

// Context
import { AuthContext } from 'authentication/AuthContext';
import { DrawerContext } from 'common/drawer/DrawerContext';

// Utils
import { getSelectionURI } from 'utils/helpers/url';
import { useUserRolePermissions } from 'hooks';
import { tooltipLinkStyles, tooltipStyles } from './EditPanel.styles';
import {
  Authorizations,
  ConfirmationDialog,
  FutureSequenced,
  ProductLocationInfo,
  Sequenced,
} from './components';

const EditPanel = props => {
  const {
    locationId,
    productId,
    onClose,
    details,
    detailsPending,
    fetchAuthorizationSequencingDetails,
    setFilters,
    setActiveMyAuthTab,
    setActiveSelfAuthTab,
    setActiveSupplierAuthTab,
    updateAuthorizationSequencingDetails,
    updateSequence,
  } = props;
  const {
    futureSequencedAuthorizations,
    futureUnSequencedAuthorizations,
    futureSequencedAuthorizationDate,
    product,
    location,
    dataCompany,
    sequencedAuthorizations,
    unsequencedAuthorizations,
  } = details;
  const {
    currentAffiliate: { dataCompanyId, customerIdName: companyCustomerIdName },
  } = useContext(AuthContext);
  const { ref } = useContext(DrawerContext);
  const { isProductLoadingManage } = useUserRolePermissions();
  const { pathname } = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('baseProducts');
  const theme = useTheme();

  const title = isProductLoadingManage ? 'Edit Sequence' : 'View Sequence';

  useEffect(() => {
    if (product?.type === 'Custom') setActiveTab('customProducts');
  }, [product]);

  useEffect(() => {
    fetchAuthorizationSequencingDetails({
      locationId,
      productId,
      supplier: {
        companyCustomerIdName,
        dataCompanyId,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (futureSequencedAuthorizations !== undefined && futureSequencedAuthorizations.length > 0)
      return;

    const futureSequencesFromSeq = [
      ...sequencedAuthorizations.filter(sequence => !!sequence.futureSequenceNumber),
      ...unsequencedAuthorizations.filter(sequence => !!sequence.futureSequenceNumber),
    ].sort((a, b) => {
      if (
        a.futureSequenceNumber === b.futureSequenceNumber ||
        R.isEmpty(a.futureSequenceNumber) ||
        R.isEmpty(b.futureSequenceNumber)
      ) {
        return 0;
      }
      return a.futureSequenceNumber < b.futureSequenceNumber ? -1 : 1;
    });
    updateSequence({ key: 'futureSequencedAuthorizations', value: futureSequencesFromSeq });
    updateSequence({
      key: 'futureSequencedAuthorizationDate',
      value: futureSequencesFromSeq[0]?.futureSequenceDate
        ? new Date(futureSequencesFromSeq[0]?.futureSequenceDate)
        : addDays(new Date(), 1),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequencedAuthorizations, unsequencedAuthorizations, updateSequence]);

  const handleClick = (inventoryOwner, supplierDirectedBlend, url) => {
    supplierDirectedBlend && setActiveSelfAuthTab('supplierDirectedBlends');
    !supplierDirectedBlend &&
      setFilters({
        name: 'tempCardFilter',
        value: { location: location.nameShort ?? 'Any', product: product.nameShort ?? 'Any' },
      });
    setFilters({
      name: 'tempSearchTerm',
      value: supplierDirectedBlend ? location.nameShort : inventoryOwner.nameShort,
    });
    // if it is not a supplier directed blend and the URL is directing
    // to a certain module, set the active tab in the module to the
    // correct tab for the product selected
    if (!supplierDirectedBlend && url.includes('my-authorizations')) {
      setActiveMyAuthTab(activeTab);
    } else if (!supplierDirectedBlend && url.includes('supplier')) {
      setActiveSupplierAuthTab(activeTab);
    }

    onClose();
  };

  const getUrlPrefix = (inventoryOwner, supplierDirectedBlend) => {
    const affiliateIs = R.both(
      R.propEq('dataCompanyId', dataCompanyId),
      R.propEq('companyCustomerIdName', companyCustomerIdName)
    );
    if (supplierDirectedBlend) {
      return 'self-authorizations/location/';
    }
    if (affiliateIs(inventoryOwner)) {
      return 'supplier-authorizations/supplier/';
    }
    return 'my-authorizations/inventory-owner/';
  };

  const badgeProps = (active, inventoryOwner, supplierDirectedBlend = false) => {
    const encodedStr = getSelectionURI(supplierDirectedBlend ? location : inventoryOwner);
    const urlPrefix = getUrlPrefix(
      inventoryOwner,
      supplierDirectedBlend,
      dataCompanyId,
      companyCustomerIdName
    );

    const href = R.compose(
      R.join('/'),
      R.append(`${urlPrefix}${encodedStr}`),
      R.take(5),
      R.split('/')
    )(pathname);

    return {
      tag: active ? 'ON' : 'OFF',
      title: (
        <>
          {`Supplier Authorization currently turned ${active ? 'on' : 'off'}`}.&nbsp;
          <Link
            onClick={() => handleClick(inventoryOwner, supplierDirectedBlend, urlPrefix)}
            sx={{ ...tooltipLinkStyles }}
            to={href}
          >
            View
          </Link>
        </>
      ),
      tooltipProps: { classes: { tooltip: tooltipStyles(theme) }, placement: 'top-start' },
      type: active ? 'green' : '',
    };
  };

  const handleSubmit = () => {
    updateAuthorizationSequencingDetails();
    setDialogOpen(false);
    onClose();
  };

  const handleOnMove = (source, destination) => {
    const updatedArr = R.move(source, destination, sequencedAuthorizations);
    updateSequence({ key: 'sequencedAuthorizations', value: updatedArr });
  };

  const handleOnFutureMove = (source, destination) => {
    const updatedArr = R.move(source, destination, futureSequencedAuthorizations);
    updateSequence({ key: 'futureSequencedAuthorizations', value: updatedArr });
  };

  const handleOnFutureRemove = idx => {
    const updatedArr = R.remove(idx, 1, futureSequencedAuthorizations);
    const updatedRemoveArr = R.append(
      futureSequencedAuthorizations[idx],
      futureUnSequencedAuthorizations
    );
    updateSequence({ key: 'futureUnSequencedAuthorizations', value: updatedRemoveArr });

    return updateSequence({
      key: 'futureSequencedAuthorizations',
      value: updatedArr,
    });
  };

  const handleAddToFutureSeq = authorization => {
    const updatedArr = [...futureSequencedAuthorizations, authorization];
    const updatedRemoveArr = futureUnSequencedAuthorizations?.filter(
      auth => auth.productAuthorizationId !== authorization.productAuthorizationId
    );
    updateSequence({ key: 'futureUnSequencedAuthorizations', value: updatedRemoveArr });

    return updateSequence({
      key: 'futureSequencedAuthorizations',
      value: updatedArr,
    });
  };

  const handleRemove =
    (key1, key2 = 'sequencedAuthorizations') =>
    idx => {
      const withoutSeq = R.remove(idx, 1, details[key1]);
      updateSequence({ key: key1, value: withoutSeq });
      const authorization = R.path([key1, idx], details);
      const addAuthorization =
        key2 === 'sequencedAuthorizations' ? R.append(authorization) : R.prepend(authorization);
      const updatedArr = addAuthorization(details[key2]);
      updateSequence({ key: key2, value: updatedArr });
    };

  const handleFutureDateChange = date => {
    const formattedDate = new Date(date);
    updateSequence({ key: 'futureSequencedAuthorizationDate', value: formattedDate });
  };

  return (
    <DrawerLayoutWrapper
      buttons={
        isProductLoadingManage && (
          <Button onClick={() => setDialogOpen(true)}>Submit Sequence</Button>
        )
      }
      headerButtonText="CLOSE"
      isLoading={detailsPending}
      onHeaderButtonClick={onClose}
      title={title}
    >
      <Box flexGrow={1}>
        <ProductLocationInfo
          canEdit={isProductLoadingManage}
          dataCompany={dataCompany}
          location={location}
          product={product}
        />
        <Sequenced
          badgeProps={badgeProps}
          canEdit={isProductLoadingManage}
          onMove={handleOnMove}
          onRemoveSeq={handleRemove('sequencedAuthorizations', 'unsequencedAuthorizations')}
          sequencedAuthorizations={sequencedAuthorizations}
        />
        {futureSequencedAuthorizations && (
          <FutureSequenced
            badgeProps={badgeProps}
            canEdit={isProductLoadingManage}
            futureDate={futureSequencedAuthorizationDate}
            futureSequences={futureSequencedAuthorizations}
            onDateChange={handleFutureDateChange}
            onMove={handleOnFutureMove}
            onRemoveSeq={handleOnFutureRemove}
          />
        )}
        {futureSequencedAuthorizations && (
          <Authorizations
            authorizations={[...sequencedAuthorizations, ...unsequencedAuthorizations]}
            badgeProps={badgeProps}
            canEdit={isProductLoadingManage}
            futureSequencedAuthorizations={R.pluck(
              'productAuthorizationId',
              futureSequencedAuthorizations
            )}
            onAddFutureSeq={handleAddToFutureSeq}
            onRemoveSeq={handleRemove('unsequencedAuthorizations')}
            sequencedAuthorizations={sequencedAuthorizations}
            unsequencedAuthorizations={unsequencedAuthorizations}
          />
        )}
      </Box>
      {dialogOpen && (
        <Portal container={ref.current}>
          <ConfirmationDialog
            location={location}
            onClose={setDialogOpen}
            onConfirm={handleSubmit}
            product={product}
          />
        </Portal>
      )}
    </DrawerLayoutWrapper>
  );
};

export default EditPanel;

EditPanel.propTypes = {
  details: shape({}),
  detailsPending: bool.isRequired,
  fetchAuthorizationSequencingDetails: func.isRequired,
  locationId: number.isRequired,
  onClose: func.isRequired,
  productId: number.isRequired,
  setActiveMyAuthTab: func.isRequired,
  setActiveSelfAuthTab: func.isRequired,
  setActiveSupplierAuthTab: func.isRequired,
  setFilters: func.isRequired,
  updateAuthorizationSequencingDetails: func.isRequired,
  updateSequence: func.isRequired,
};

EditPanel.defaultProps = {
  details: {},
};
